import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
  fetchPublicTokens,
  createPublicToken,
  updatePublicToken,
  deletePublicToken,
  copyToClipboard,
  PublicToken,
} from '_/api/publicTokens/publicTokens.api';
import { notification } from 'antd';

export const PUBLIC_TOKENS_QUERY_KEY = ['publicTokens'];

/**
 * Hook to fetch public tokens
 */
export const usePublicTokens = () => {
  return useQuery({
    queryKey: PUBLIC_TOKENS_QUERY_KEY,
    queryFn: fetchPublicTokens,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to create a public token
 */
export const useCreatePublicToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicToken: { name: string }) => createPublicToken(publicToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLIC_TOKENS_QUERY_KEY });
      notification.success({
        message: 'Public token created',
      });
    },
    onError: (error: any) => {
      console.error('Error creating public token', error);
      const message = error?.message || 'Error creating public token';
      notification.error({
        message: message,
      });
    },
  });
};

/**
 * Hook to update a public token
 */
export const useUpdatePublicToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ publicTokenId, publicToken }: { publicTokenId: string; publicToken: Partial<PublicToken> }) =>
      updatePublicToken(publicTokenId, publicToken),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLIC_TOKENS_QUERY_KEY });
      notification.success({
        message: 'Public token updated',
      });
    },
    onError: (error: any) => {
      console.error('Error updating public token', error);
      notification.error({
        message: error?.message || 'Error updating public token',
      });
    },
  });
};

/**
 * Hook to delete a public token
 */
export const useDeletePublicToken = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (publicTokenId: string) => deletePublicToken(publicTokenId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: PUBLIC_TOKENS_QUERY_KEY });
      notification.success({
        message: 'Public token deleted',
      });
    },
    onError: (error: any) => {
      console.error('Error deleting public token', error);
      notification.error({
        message: error?.message || 'Error deleting public token',
      });
    },
  });
};

/**
 * Hook to copy URL to clipboard
 */
export const useCopyToClipboard = () => {
  return useMutation({
    mutationFn: (text: string) => copyToClipboard(text),
    onSuccess: () => {
      notification.success({
        message: 'Copied to clipboard',
      });
    },
    onError: (error: any) => {
      console.error('Error copying to clipboard', error);
      notification.error({
        message: 'Failed to copy to clipboard',
      });
    },
  });
};
